import { SignedIn, SignedOut } from '@daveyplate/better-auth-ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useSession } from '@/hooks/authHooks';
import { SubscriptionTier, useSubscription } from '@/hooks/useSubscription';

type Feature = 'alerts' | 'graphs';

interface RequireSubscriptionProps {
  children: React.ReactNode;
  /**
   * Minimum tier required to access this feature.
   * 'free' = all authenticated users
   * 'pro' = pro, premium, and legacy users
   * 'premium' = only premium users
   */
  requiredTier?: SubscriptionTier;
  /**
   * Feature being accessed - used for more specific checks
   * 'alerts' = any tier (free has 1 alert, pro has 3, premium has 20, legacy/VIP has 20)
   * 'graphs' = any tier (including free)
   */
  feature?: Feature;
  /**
   * Custom redirect path when subscription check fails
   */
  redirectTo?: string;
}

const TIER_HIERARCHY: Record<SubscriptionTier, number> = {
  free: 0,
  pro: 1,
  premium: 2,
  legacy: 1, // Legacy tier has similar access to pro
};

function hasSufficientTier(
  currentTier: SubscriptionTier,
  requiredTier: SubscriptionTier
): boolean {
  return TIER_HIERARCHY[currentTier] >= TIER_HIERARCHY[requiredTier];
}

function getRequiredTierForFeature(feature: Feature): SubscriptionTier {
  switch (feature) {
    case 'alerts':
      // Free tier now has 1 alert, so alerts are accessible to all tiers
      return 'free';
    case 'graphs':
      return 'free';
    default:
      return 'free';
  }
}

function SubscriptionGate({
  children,
  requiredTier = 'free',
  feature,
  redirectTo = '/upgrade',
}: RequireSubscriptionProps) {
  const navigate = useNavigate();
  const { isPending: sessionPending } = useSession();
  const {
    subscription,
    isLoading: subscriptionLoading,
    tier,
    error,
    refetch,
  } = useSubscription();

  // Determine the actual required tier
  const effectiveRequiredTier = feature
    ? getRequiredTierForFeature(feature)
    : requiredTier;

  const isLoading = sessionPending || subscriptionLoading;
  const hasAccess =
    subscription && hasSufficientTier(tier, effectiveRequiredTier);

  // Check if there's a pending plan in sessionStorage (user just signed up)
  const hasPendingPlan =
    typeof window !== 'undefined' &&
    Boolean(sessionStorage.getItem('selectedPlan'));

  useEffect(() => {
    // Don't redirect if there's a pending plan being processed
    if (hasPendingPlan) {
      return;
    }

    // If there was an error, try refetching once (server might have been restarting)
    if (error && !isLoading) {
      const timer = setTimeout(() => {
        refetch();
      }, 1000);
      return () => clearTimeout(timer);
    }

    // Only redirect after loading is complete and we know user doesn't have access
    if (!isLoading && !error && subscription && !hasAccess) {
      navigate({ to: redirectTo });
    }
  }, [
    isLoading,
    error,
    subscription,
    hasAccess,
    hasPendingPlan,
    navigate,
    redirectTo,
    refetch,
  ]);

  // Show nothing while loading or if there's a pending plan
  if (isLoading || hasPendingPlan) {
    return null;
  }

  // If there's an error, show a retry message
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  // If subscription check passed, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // While redirecting, show nothing
  return null;
}

/**
 * RequireSubscription - Guards content behind authentication AND subscription checks.
 *
 * Usage:
 * - For alert features (all tiers can access): <RequireSubscription feature="alerts">...</RequireSubscription>
 * - For graph features (all tiers): <RequireSubscription feature="graphs">...</RequireSubscription>
 * - For specific tier: <RequireSubscription requiredTier="premium">...</RequireSubscription>
 */
export function RequireSubscription({
  children,
  requiredTier = 'free',
  feature,
  redirectTo = '/upgrade',
}: RequireSubscriptionProps) {
  return (
    <>
      {/* Redirect unauthenticated users to upgrade page first */}
      <SignedOut>
        <RedirectToUpgrade />
      </SignedOut>

      {/* For authenticated users, check subscription */}
      <SignedIn>
        <SubscriptionGate
          requiredTier={requiredTier}
          feature={feature}
          redirectTo={redirectTo}>
          {children}
        </SubscriptionGate>
      </SignedIn>
    </>
  );
}

/**
 * RequireAuth - Simple auth check without subscription requirement.
 * Redirects to upgrade page if not authenticated.
 */
export function RequireAuthWithUpgrade({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <>
      <SignedOut>
        <RedirectToUpgrade />
      </SignedOut>
      <SignedIn>{children}</SignedIn>
    </>
  );
}

/**
 * Redirect to upgrade page for unauthenticated users
 */
function RedirectToUpgrade() {
  const navigate = useNavigate();

  useEffect(() => {
    navigate({ to: '/upgrade' });
  }, [navigate]);

  return null;
}
